import React, { useState } from "react";
import { Link } from "react-router-dom";
import { X, ChevronRight, User, Package, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { FASHION_CATEGORIES } from "@/lib/categories";

export function MobileMenu({ open, onClose }) {
  const { user, setAuthOpen } = useAuth();
  const [expanded, setExpanded] = useState(null); // "women" | "men" | null

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/40 z-50"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-y-0 left-0 w-[85%] max-w-[380px] bg-white z-50 flex flex-col overflow-y-auto"
          >
            {/* Close & Logo */}
            <div className="flex items-center justify-between px-5 h-14 border-b border-fashion-border">
              <span className="font-display text-lg font-light tracking-[0.12em] uppercase">
                Farniymart
              </span>
              <button onClick={onClose} className="p-1 text-fashion-text">
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>

            {/* Categories */}
            <div className="flex-1 px-5 py-6 space-y-1">
              {FASHION_CATEGORIES.map((cat) => (
                <div key={cat.slug}>
                  <button
                    onClick={() => setExpanded(expanded === cat.slug ? null : cat.slug)}
                    className="w-full flex items-center justify-between py-3 text-[15px] font-medium tracking-[0.05em] uppercase text-fashion-text"
                  >
                    {cat.name}
                    <ChevronRight
                      className={`h-4 w-4 text-fashion-text-light transition-transform duration-300 ${
                        expanded === cat.slug ? "rotate-90" : ""
                      }`}
                    />
                  </button>

                  <AnimatePresence>
                    {expanded === cat.slug && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pl-3 pb-3 space-y-0.5">
                          <Link
                            to={`/${cat.slug}`}
                            onClick={onClose}
                            className="block py-2 text-[13px] font-medium text-fashion-accent-warm"
                          >
                            View All {cat.name}
                          </Link>
                          {cat.subcategories.map((sub) => (
                            <Link
                              key={sub.slug}
                              to={`/${cat.slug}/${sub.slug}`}
                              onClick={onClose}
                              className="block py-2 text-[13px] text-fashion-text-muted hover:text-fashion-text transition-colors"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              <div className="fashion-divider my-4" />

              <Link
                to="/new-arrivals"
                onClick={onClose}
                className="block py-3 text-[15px] font-medium tracking-[0.05em] uppercase text-fashion-text"
              >
                New Arrivals
              </Link>
              <Link
                to="/collections"
                onClick={onClose}
                className="block py-3 text-[15px] font-medium tracking-[0.05em] uppercase text-fashion-text"
              >
                Collections
              </Link>

              <div className="fashion-divider my-4" />

              {/* Account Links */}
              {user ? (
                <div className="space-y-1">
                  <Link
                    to="/account"
                    onClick={onClose}
                    className="flex items-center gap-3 py-3 text-sm text-fashion-text-muted"
                  >
                    <User className="h-4 w-4" strokeWidth={1.5} /> My Account
                  </Link>
                  <Link
                    to="/orders"
                    onClick={onClose}
                    className="flex items-center gap-3 py-3 text-sm text-fashion-text-muted"
                  >
                    <Package className="h-4 w-4" strokeWidth={1.5} /> My Orders
                  </Link>
                  <Link
                    to="/wishlist"
                    onClick={onClose}
                    className="flex items-center gap-3 py-3 text-sm text-fashion-text-muted"
                  >
                    <Heart className="h-4 w-4" strokeWidth={1.5} /> Wishlist
                  </Link>
                </div>
              ) : (
                <button
                  onClick={() => { onClose(); setAuthOpen(true); }}
                  className="flex items-center gap-3 py-3 text-sm text-fashion-text-muted"
                >
                  <User className="h-4 w-4" strokeWidth={1.5} /> Sign In / Register
                </button>
              )}
            </div>

            {/* Bottom */}
            <div className="px-5 py-4 border-t border-fashion-border">
              <p className="text-[11px] text-fashion-text-light tracking-wide">
                Fashion First. Nothing Else.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
